// Booking details and actions for staff: done, no-show, undo, cancel (with reason), move.
import { useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { StaffBooking } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { message } from '../lib/errors.ts';
import { dateOf, thaiDateTime, timeOf } from '../lib/time.ts';
import { Button, Chip, Field, Sheet, useToast } from '../ui/index.tsx';
import { NeedsConfirmDialog, type SlotChoice, StaffSlotPicker } from './pickers.tsx';
import { STATUS_LABEL } from './status.ts';

const REASONS = ['ช่างไม่สบาย', 'ร้านปิดฉุกเฉิน', 'อื่นๆ'];

type Mode = 'view' | 'cancel' | 'move';

export function BookingSheet({ booking, onClose, onChanged }: { booking: StaffBooking | null; onClose: () => void; onChanged: () => void }) {
  const { api, shop, isOwner, me } = useShop();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('view');
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState<string | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const [choice, setChoice] = useState<SlotChoice | null>(null);
  const [needs, setNeeds] = useState<{ outsideHours?: boolean; timeOff?: boolean } | null>(null);

  if (!booking) return null;
  const b = booking;
  const close = () => { setMode('view'); setPreset(null); setOtherReason(''); setChoice(null); setNeeds(null); onClose(); };
  const started = Date.now() >= new Date(b.startAt).getTime();

  async function act(action: 'complete' | 'no-show' | 'undo' | 'cancel' | 'move', extra: Record<string, unknown> = {}, okText = 'บันทึกแล้ว') {
    setBusy(true);
    try {
      await api.staff.bookingAction(b.id, action, { version: b.version, ...extra });
      toast(okText, 'success');
      close();
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'NEEDS_CONFIRM') {
        setNeeds((e.details ?? {}) as { outsideHours?: boolean; timeOff?: boolean });
      } else {
        toast(message(e), 'error');
        if (e instanceof ApiError && (e.code === 'VERSION_CONFLICT' || e.code === 'NOT_FOUND')) { close(); onChanged(); }
      }
    } finally {
      setBusy(false);
    }
  }

  const reason = preset === 'อื่นๆ' ? otherReason.trim() : preset ?? '';
  const service = shop.services.find((s) => s.id === b.serviceId);
  const movable = service?.barberIds ?? [b.barberId];
  const ownOnlyBarber = me.staff?.role === 'barber';

  return (
    <Sheet open title={b.customerName} onClose={close}>
      <div className="stack">
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Chip tone={STATUS_LABEL[b.status].tone}>{STATUS_LABEL[b.status].label}{b.autoCompleted ? ' (อัตโนมัติ)' : ''}</Chip>
          {b.noShowCount > 0 && <Chip tone="accent">เคยไม่มา {b.noShowCount} ครั้ง</Chip>}
          {b.sharedPhone && <Chip>เบอร์ซ้ำ</Chip>}
          {!b.hasLine && <Chip tone="muted">ไม่มี LINE</Chip>}
        </div>
        <dl className="summary">
          <dt>เวลา</dt><dd>{thaiDateTime(b.startAt)} – {timeOf(b.endAt)} น.</dd>
          <dt>บริการ</dt><dd>{b.serviceName} · {b.price.toLocaleString('th-TH')} บาท</dd>
          <dt>ช่าง</dt><dd>{b.barberName}</dd>
          {b.phone && <><dt>โทร</dt><dd><a href={`tel:${b.phone}`}>{b.phone}</a></dd></>}
          {b.note && <><dt>หมายเหตุลูกค้า</dt><dd>{b.note}</dd></>}
          {b.cancelReason && <><dt>เหตุผลยกเลิก</dt><dd>{b.cancelReason}</dd></>}
        </dl>

        {mode === 'view' && b.status === 'confirmed' && (
          <div className="stack">
            <div className="row">
              <Button block loading={busy} onClick={() => act('complete', {}, 'บันทึกว่าเสร็จแล้ว')}>เสร็จแล้ว</Button>
              <Button block variant="secondary" disabled={busy || !started} onClick={() => act('no-show', {}, 'บันทึกว่าไม่มา')}>ไม่มา</Button>
            </div>
            <Button block variant="secondary" disabled={busy} onClick={() => setMode('move')}>ย้ายเวลา/ช่าง</Button>
            <Button block variant="ghost" disabled={busy} onClick={() => setMode('cancel')}>ยกเลิกการจอง</Button>
          </div>
        )}

        {mode === 'view' && (b.status === 'completed' || b.status === 'no_show') && (
          <div className="stack">
            <Button block variant="secondary" loading={busy} onClick={() => act('undo', {}, 'ย้อนกลับแล้ว')}>ย้อนกลับเป็นยืนยัน</Button>
            {isOwner && b.status === 'completed' && b.autoCompleted && (
              <Button block variant="ghost" disabled={busy} onClick={() => act('no-show', {}, 'บันทึกว่าไม่มา')}>เปลี่ยนเป็นไม่มา</Button>
            )}
          </div>
        )}

        {mode === 'cancel' && (
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="section-title" style={{ marginTop: 0 }}>เหตุผลที่ยกเลิก (ลูกค้าจะได้รับแจ้ง)</legend>
            {REASONS.map((r) => (
              <label key={r} className="check">
                <input type="radio" name="cancel-reason" checked={preset === r} onChange={() => setPreset(r)} />
                <span>{r}</span>
              </label>
            ))}
            {preset === 'อื่นๆ' && (
              <Field label="ระบุเหตุผล">
                <textarea value={otherReason} onChange={(e) => setOtherReason(e.target.value)} maxLength={200} />
              </Field>
            )}
            <div className="row">
              <Button variant="ghost" onClick={() => setMode('view')}>กลับ</Button>
              <Button variant="danger" block loading={busy} disabled={reason === ''} onClick={() => act('cancel', { reason }, 'ยกเลิกการจองแล้ว')}>ยืนยันยกเลิก</Button>
            </div>
          </fieldset>
        )}

        {mode === 'move' && (
          <div>
            <StaffSlotPicker api={api} shop={shop} serviceId={b.serviceId} barberIds={movable}
              fixedBarberId={ownOnlyBarber && movable.length <= 1 ? b.barberId : undefined}
              initialBarberId={b.barberId} initialDate={dateOf(b.startAt)} onChange={setChoice} />
            <div className="row">
              <Button variant="ghost" onClick={() => setMode('view')}>กลับ</Button>
              <Button block loading={busy} disabled={!choice} onClick={() => choice && act('move', { barberId: choice.barberId, startAt: choice.startAt }, 'ย้ายการจองแล้ว')}>ย้ายการจอง</Button>
            </div>
          </div>
        )}
      </div>
      <NeedsConfirmDialog details={needs} loading={busy}
        onCancel={() => setNeeds(null)}
        onConfirm={() => {
          const flags = { confirmOutsideHours: needs?.outsideHours === true, confirmTimeOff: needs?.timeOff === true };
          setNeeds(null);
          if (choice) void act('move', { barberId: choice.barberId, startAt: choice.startAt, ...flags }, 'ย้ายการจองแล้ว');
        }} />
    </Sheet>
  );
}
